import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import * as DS from '@styles/DesignSystem.styles';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  variant?: 'default' | 'outlined' | 'elevated';
  padding?: 'sm' | 'md' | 'lg';
}

const StyledCard = styled(DS.Card)<{ variant: string; padding: string }>`
  margin-bottom: ${theme.spacing.md};
  background: ${theme.colors.surface}; /* Use surface color from design system */

  ${props => {
    switch (props.variant) {
      case 'outlined':
        return `
          border: 1px solid ${theme.colors.border};
          box-shadow: none;
        `;
      case 'elevated':
        return `
          box-shadow: ${theme.shadows.lg};
          &:hover {
            box-shadow: ${theme.shadows.xl};
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          box-shadow: ${theme.shadows.md};
        `;
    }
  }}

  padding: ${props => {
    switch (props.padding) {
      case 'sm':
        return theme.spacing.md;
      case 'lg':
        return theme.spacing.xl;
      default:
        return theme.spacing.lg;
    }
  }};
`;

const CardHeaderWrapper = styled(DS.CardHeader)`
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
`;

const CardTitleStyled = styled(DS.CardTitle)`
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  margin: ${theme.spacing.xs} 0 0 0;
`;

const CardContent = styled(DS.CardBody)`
  /* Content styles */
`;

const CardActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  children,
  actions,
  variant = 'default',
  padding = 'md',
  ...rest // important !
}) => {
  return (
    <StyledCard variant={variant} padding={padding} {...rest}>
      {(title || subtitle) && (
        <CardHeaderWrapper>
          {title && <CardTitleStyled>{title}</CardTitleStyled>}
          {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        </CardHeaderWrapper>
      )}
      <CardContent>{children}</CardContent>
      {actions && <CardActions>{actions}</CardActions>}
    </StyledCard>
  );
};